import React, { useEffect, useState } from "react";
import { RefreshCw, Play, Save, Trash2, AlertCircle, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

const DEFAULT_QUERIES = [
  "lego star wars", "lego technic", "lego icons", "lego creator",
  "lego ideas", "lego harry potter", "lego architecture", "lego marvel",
  "lego super mario", "lego friends",
];

export default function EbayAdminTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [draft, setDraft] = useState(null);
  const [runResult, setRunResult] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/admin/ebay/status");
      setStatus(r.data);
      setDraft({
        ebay_enabled: r.data.enabled,
        ebay_min_discount: r.data.min_discount,
        ebay_interval_minutes: r.data.interval_minutes,
        ebay_max_per_run: r.data.max_per_run,
        ebay_min_seller_feedback: r.data.min_seller_feedback,
        ebay_autopublish: r.data.autopublish,
        ebay_draft_ttl_hours: r.data.draft_ttl_hours ?? 48,
        ebay_queries: (r.data.queries && r.data.queries.length ? r.data.queries : DEFAULT_QUERIES).join("\n"),
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...draft,
        ebay_queries: draft.ebay_queries.split("\n").map((s) => s.trim()).filter(Boolean),
        ebay_min_discount: parseInt(draft.ebay_min_discount, 10),
        ebay_interval_minutes: parseInt(draft.ebay_interval_minutes, 10),
        ebay_max_per_run: parseInt(draft.ebay_max_per_run, 10),
        ebay_min_seller_feedback: parseFloat(draft.ebay_min_seller_feedback),
        ebay_draft_ttl_hours: parseInt(draft.ebay_draft_ttl_hours, 10),
      };
      await api.put("/admin/settings", payload);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    setError("");
    setRunResult(null);
    try {
      const r = await api.post("/admin/ebay/run-now", {});
      setRunResult(r.data);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setRunning(false);
    }
  };

  const wipeImported = async () => {
    if (!window.confirm("Eliminare TUTTE le offerte importate da eBay API? Non puoi annullare.")) return;
    try {
      const r = await api.delete("/admin/ebay/deals");
      alert(`Eliminate ${r.data.deleted} offerte eBay.`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  if (loading) return <div className="text-zinc-400 p-4">Caricamento eBay…</div>;
  if (error && (!status || !draft)) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 max-w-2xl">
        <div className="flex items-center gap-2 text-red-300 font-bold mb-2">
          <AlertCircle size={16} /> Impossibile caricare il pannello eBay
        </div>
        <p className="text-sm text-red-200/80 mb-3">
          {typeof error === "string" ? error : JSON.stringify(error)}
        </p>
        <p className="text-xs text-zinc-400 mb-3">
          Cause comuni: il backend non ha ricaricato il nuovo codice (serve un deploy aggiornato),
          oppure le credenziali eBay non sono ancora caricate nelle variabili di ambiente di produzione.
        </p>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white text-xs font-bold px-3 py-2 rounded-lg"
        >
          <RefreshCw size={12} /> Riprova
        </button>
      </div>
    );
  }
  if (!status || !draft) return <div className="text-zinc-400 p-4">Nessun dato.</div>;

  const stats = status.last_run_stats;

  return (
    <div data-testid="ebay-admin-tab" className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display font-black text-2xl text-white">Import automatico eBay</h1>
          <p className="text-zinc-500 text-sm">
            Pesca le offerte LEGO direttamente dall&apos;API ufficiale eBay con il tuo Campaign ID affiliato.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${status.credentials_configured ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" : "bg-red-500/10 text-red-400 border border-red-500/30"}`}>
            {status.credentials_configured ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
            {status.credentials_configured ? "Credenziali OK" : "Credenziali mancanti"}
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-zinc-900 text-zinc-300 border border-zinc-800">
            Campaign: {status.campaign_id || "—"}
          </span>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300">
          {typeof error === "string" ? error : JSON.stringify(error)}
        </div>
      )}

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          data-testid="ebay-run-now"
          onClick={runNow}
          disabled={running || !status.credentials_configured}
          className="inline-flex items-center gap-2 bg-[#FBD51C] hover:bg-[#FFE14A] disabled:opacity-40 text-black font-bold text-sm px-4 py-2.5 rounded-lg"
        >
          <Play size={14} /> {running ? "In esecuzione…" : "Esegui ora"}
        </button>
        <button
          data-testid="ebay-refresh"
          onClick={load}
          className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white text-xs font-bold px-3 py-2.5 rounded-lg"
        >
          <RefreshCw size={12} /> Aggiorna stato
        </button>
        <button
          data-testid="ebay-wipe-imported"
          onClick={wipeImported}
          className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-red-500/20 border border-zinc-800 hover:border-red-500/40 text-zinc-400 hover:text-red-300 text-xs font-bold px-3 py-2.5 rounded-lg"
        >
          <Trash2 size={12} /> Cancella offerte eBay importate
        </button>
      </div>

      {/* Run result */}
      {runResult && runResult.ok && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
          <p className="text-sm text-emerald-300 font-bold mb-2">
            ✅ Run completato — {runResult.stats?.inserted} nuove offerte inserite ({runResult.stats?.duplicates} duplicati saltati)
          </p>
          <pre className="text-[11px] text-emerald-200/70 overflow-x-auto">{JSON.stringify(runResult.stats, null, 2)}</pre>
        </div>
      )}

      {/* Settings card */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-5">
        <h2 className="font-display font-black text-lg text-white">Configurazione</h2>

        <Toggle
          testId="ebay-toggle-enabled"
          label="Polling automatico attivo"
          hint="Se ON, il backend interroga eBay periodicamente e importa nuove offerte."
          checked={draft.ebay_enabled}
          onChange={(v) => setDraft({ ...draft, ebay_enabled: v })}
        />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <NumberField
            testId="ebay-min-discount"
            label="Sconto minimo %"
            value={draft.ebay_min_discount}
            onChange={(v) => setDraft({ ...draft, ebay_min_discount: v })}
            min={5}
            max={90}
          />
          <NumberField
            testId="ebay-interval"
            label="Intervallo (minuti)"
            value={draft.ebay_interval_minutes}
            onChange={(v) => setDraft({ ...draft, ebay_interval_minutes: v })}
            min={30}
            max={1440}
          />
          <NumberField
            testId="ebay-max-per-run"
            label="Max offerte per run"
            value={draft.ebay_max_per_run}
            onChange={(v) => setDraft({ ...draft, ebay_max_per_run: v })}
            min={1}
            max={100}
          />
          <NumberField
            testId="ebay-min-feedback"
            label="Feedback venditore min %"
            value={draft.ebay_min_seller_feedback}
            onChange={(v) => setDraft({ ...draft, ebay_min_seller_feedback: v })}
            min={0}
            max={100}
            step={0.5}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <NumberField
            testId="ebay-draft-ttl"
            label="Auto-cancella bozze dopo (ore)"
            value={draft.ebay_draft_ttl_hours}
            onChange={(v) => setDraft({ ...draft, ebay_draft_ttl_hours: v })}
            min={0}
            max={720}
          />
          <div className="text-[11px] text-zinc-500 self-end pb-3">
            Le bozze non pubblicate vengono cancellate dopo X ore (default 48h). Imposta 0 per disattivare.
          </div>
        </div>

        <Toggle
          testId="ebay-autopublish"
          label="Pubblica automaticamente"
          hint="Se OFF, le offerte importate restano in stato bozza (non visibili sul sito) finché non le approvi manualmente."
          checked={draft.ebay_autopublish}
          onChange={(v) => setDraft({ ...draft, ebay_autopublish: v })}
        />

        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
            Keyword di ricerca (una per riga)
          </label>
          <textarea
            data-testid="ebay-queries"
            rows={6}
            value={draft.ebay_queries}
            onChange={(e) => setDraft({ ...draft, ebay_queries: e.target.value })}
            placeholder="lego star wars&#10;lego technic&#10;..."
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60 text-sm font-mono"
          />
          <p className="text-[11px] text-zinc-500 mt-1">
            Ogni keyword diventa una ricerca eBay separata. Più keyword = più offerte ma più tempo per girare.
          </p>
        </div>

        <button
          data-testid="ebay-save"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-[#FBD51C] hover:bg-[#FFE14A] disabled:opacity-40 text-black font-bold text-sm px-4 py-2.5 rounded-lg"
        >
          <Save size={14} /> {saving ? "Salvataggio…" : "Salva configurazione"}
        </button>
      </div>

      {/* Last run summary */}
      {stats && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
          <h2 className="font-display font-black text-lg text-white mb-3">Ultima esecuzione</h2>
          <p className="text-xs text-zinc-500 mb-4">
            {status.last_run_at ? new Date(status.last_run_at).toLocaleString("it-IT") : "—"}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Stat label="Inserite" value={stats.inserted ?? 0} accent="emerald" />
            <Stat label="Duplicati" value={stats.duplicates ?? 0} />
            <Stat label="Bozze scadute" value={stats.drafts_auto_deleted ?? 0} accent="amber" />
            <Stat label="Sotto soglia" value={stats.filtered_below_threshold ?? 0} />
            <Stat label="Senza sconto" value={stats.filtered_no_discount ?? 0} />
            <Stat label="Non-LEGO scartate" value={stats.filtered_non_lego ?? 0} />
            <Stat label="Feedback basso" value={stats.filtered_low_feedback ?? 0} />
            <Stat label="Raw items eBay" value={stats.raw_items ?? 0} />
            <Stat label="Query eseguite" value={stats.queries_run ?? 0} />
          </div>
          {stats.by_confidence && (
            <div className="mt-4 text-xs text-zinc-400">
              Sconto rilevato via:&nbsp;
              <span className="text-emerald-400 font-bold">{stats.by_confidence.stp || 0} STP eBay</span>
              &nbsp;·&nbsp;
              <span className="text-amber-400 font-bold">{stats.by_confidence.msrp || 0} MSRP LEGO</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Toggle({ label, hint, checked, onChange, testId }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        data-testid={testId}
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 w-4 h-4 accent-[#FBD51C]"
      />
      <div>
        <div className="text-sm font-bold text-white">{label}</div>
        {hint && <div className="text-[11px] text-zinc-500">{hint}</div>}
      </div>
    </label>
  );
}

function NumberField({ label, value, onChange, min, max, step = 1, testId }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">{label}</label>
      <input
        data-testid={testId}
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        min={min}
        max={max}
        step={step}
        className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60"
      />
    </div>
  );
}

function Stat({ label, value, accent }) {
  const color =
    accent === "emerald" ? "text-emerald-400" :
    accent === "amber" ? "text-amber-400" :
    "text-white";
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className={`text-2xl font-black ${color}`}>{value}</div>
    </div>
  );
}
